import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AppContext from 'Utils/AppContext';
import { fmtDateTime, fmtRelative } from 'Utils/formatDate';
import { getLastDataSourceTest, runDataSourceTest } from 'DataSources/datasourcesApi';
import { sourceEditorPath } from 'Utils/connectionGate';
import { useCanEdit, READ_ONLY_TITLE } from 'Auth/permissions';
import {
  KINDS,
  STALE_AFTER_MS,
  applyRestrictions,
  engineText,
  isPickable,
  kindOf,
  locationOf,
  matchesQuery,
  needsRecheck,
  sortRows,
  statusOf,
} from './sourcePickerUtils';
import './SourcePicker.css';

// One stable empty list, so a picker with no rows does not recompute every memo on each render.
const NO_ROWS = [];
const plural = (n, one, many) => `${n} ${n === 1 ? one : many || `${one}s`}`;
const errorOf = (res) => (res && (res.error || res.message)) || 'Connection test failed.';

/**
 * The ONE data source picker: every screen where a user chooses data sources mounts this with its
 * own restrictions and wording. Prototype: docs/prototypes/data-source-picker.html.
 *
 * Connection status comes from what the platform already stored (`lastTestSuccess` / `lastTestAt`
 * on every row). Opening the picker never tests anything. A test runs only when the user asks:
 * Retry / Test on a row, "Retest stale", or picking a row that has never been tested (that one row,
 * and Confirm waits for it). A success older than `staleAfterMs` is shown as stale but stays
 * pickable — the start-time gate (Utils/connectionGate) decides whether a run may go.
 *
 * Props
 *   sources            rows to choose from (default: AppContext.connections)
 *   selection          'single' | 'multiple'
 *   layout             'page' (footer sticks to the viewport) | 'inline' (scrolls inside maxHeight)
 *   restrict           [{ label, test(row), why(row) }] applied before anything renders (RULES)
 *   exclude            ids already chosen elsewhere
 *   title / subtitle   heading for 'inline' (a page host renders its own PageHeader)
 *   verb               confirm button label
 *   onConfirm(rows)    confirm mode: rows carry the freshest test verdict
 *   onCancel()
 *   selected + onSelectionChange(ids, rows)   live mode (no confirm footer): the host owns selection
 *   initialSelected    ids pre-selected in confirm mode
 *   toolbar            extra controls rendered in the toolbar (e.g. a sampling-rule select)
 *   loading            string: eligibility still loading (rows hidden, message shown)
 *   busy               confirm in progress (string label, or true)
 *   confirmDisabledReason  host rule that keeps confirm off (its text is the button's tooltip)
 *   readOnlyCanConfirm     confirm only navigates (the next screen gates itself), so a read-only
 *                          account may use it; it still never tests a connection
 *   emptyMessage       shown when no row survives the restrictions
 */
function SourcePicker({
  sources,
  selection = 'single',
  layout = 'inline',
  restrict = [],
  exclude = [],
  title = null,
  subtitle = null,
  verb = 'Select',
  onConfirm,
  onCancel,
  selected: controlledSelected,
  onSelectionChange,
  initialSelected = [],
  toolbar = null,
  loading = null,
  busy = false,
  confirmDisabledReason = null,
  readOnlyCanConfirm = false,
  emptyMessage = null,
  maxHeight = 360,
  staleAfterMs = STALE_AFTER_MS,
  concurrency = 4,
  testId = 'source-picker',
}) {
  const ctx = useContext(AppContext) || {};
  const rowsIn = sources || ctx.connections || NO_ROWS;
  // A read-only account can browse but not test (POST) or start anything (403 USER_READ_ONLY).
  const canEdit = useCanEdit();
  const multi = selection === 'multiple';
  const live = typeof onSelectionChange === 'function' && controlledSelected !== undefined;

  const { pool, hidden } = useMemo(() => applyRestrictions(rowsIn, restrict, exclude), [rowsIn, restrict, exclude]);
  const byId = useMemo(() => {
    const m = {};
    rowsIn.forEach((s) => { if (s && s.id != null) m[String(s.id)] = s; });
    return m;
  }, [rowsIn]);

  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const [sort, setSort] = useState({ key: 'name', dir: 1 });
  const [ownSelected, setOwnSelected] = useState(() => (initialSelected || []).map(String));
  const [overrides, setOverrides] = useState({});   // id → status from a test run while open
  const [storedErrors, setStoredErrors] = useState({}); // id → message of the stored failure
  const [note, setNote] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [cursor, setCursor] = useState(-1);

  const selectedIds = live ? (controlledSelected || []).map(String) : ownSelected;
  const selectedRef = useRef(selectedIds);
  selectedRef.current = selectedIds;

  const setSelected = useCallback((next) => {
    const ids = typeof next === 'function' ? next(selectedRef.current) : next;
    selectedRef.current = ids;
    if (live) onSelectionChange(ids, ids.map((id) => byId[id]).filter(Boolean));
    else setOwnSelected(ids);
  }, [live, onSelectionChange, byId]);

  const now = Date.now();
  const statusById = useMemo(() => {
    const m = {};
    pool.forEach((s) => { m[String(s.id)] = statusOf(s, overrides[String(s.id)], now, staleAfterMs); });
    return m;
    // `now` is read per render on purpose; it only shifts what counts as stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool, overrides, staleAfterMs]);

  // ---- tests: only on request, bounded concurrency, a superseded result is dropped ----
  const alive = useRef(true);
  const queue = useRef([]);
  const active = useRef(0);
  const gen = useRef({});
  const refreshTimer = useRef(null);
  useEffect(() => () => {
    alive.current = false;
    clearTimeout(refreshTimer.current);
  }, []);

  const refreshConnections = () => {
    // One platform read so every other screen sees the new verdicts; never a customer connection.
    if (typeof ctx.loadConnectionsHandler !== 'function') return;
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      if (alive.current) ctx.loadConnectionsHandler().catch(() => {});
    }, 300);
  };

  const pump = () => {
    while (alive.current && active.current < concurrency && queue.current.length) {
      const id = queue.current.shift();
      const g = gen.current[id];
      active.current += 1;
      runDataSourceTest(Number(id))
        .then((res) => (res && res.success === true
          ? { state: 'online', testedAt: Date.parse(res.testedAt) || Date.now(), stale: false }
          : { state: 'offline', testedAt: Date.parse(res && res.testedAt) || Date.now(), error: errorOf(res) }))
        .catch((e) => ({ state: 'offline', testedAt: Date.now(), error: (e && e.message) || 'Connection test failed.' }))
        .then((status) => {
          active.current -= 1;
          if (!alive.current) return;
          if (gen.current[id] === g) {
            setOverrides((o) => ({ ...o, [id]: status }));
            if (status.state === 'offline' && selectedRef.current.includes(id)) {
              setSelected((ids) => ids.filter((x) => x !== id));
              setNote(`${(byId[id] && byId[id].name) || 'A source'} failed its connection test and was removed from the selection.`);
            }
          }
          if (active.current === 0 && queue.current.length === 0) refreshConnections();
          pump();
        });
    }
  };

  const runTests = (ids) => {
    if (!canEdit) return;
    const fresh = {};
    ids.forEach((raw) => {
      const id = String(raw);
      const cur = statusById[id];
      if (!cur || cur.state === 'managed' || cur.state === 'credentials') return;
      gen.current[id] = (gen.current[id] || 0) + 1;
      const prev = cur.state === 'testing' ? cur.prev : (cur.state === 'online' || cur.state === 'offline' ? cur : null);
      fresh[id] = { state: 'testing', prev };
      queue.current = queue.current.filter((x) => x !== id).concat(id);
    });
    if (Object.keys(fresh).length === 0) return;
    setOverrides((o) => ({ ...o, ...fresh }));
    pump();
  };

  // The stored failure's message — a platform read for rows that already failed, so the reason
  // shows without testing again.
  const fetchedErrors = useRef(new Set());
  useEffect(() => {
    const failed = pool
      .filter((s) => s.lastTestSuccess === false && !(statusOf(s).error) && !fetchedErrors.current.has(String(s.id)))
      .slice(0, 50);
    if (failed.length === 0) return;
    failed.forEach((s) => fetchedErrors.current.add(String(s.id)));
    (async () => {
      for (let i = 0; i < failed.length; i += concurrency) {
        const chunk = failed.slice(i, i + concurrency);
        // eslint-disable-next-line no-await-in-loop
        const found = await Promise.all(chunk.map((s) => getLastDataSourceTest(Number(s.id))
          .then((dto) => (dto && dto.tested && !dto.success ? [String(s.id), errorOf(dto)] : null))
          .catch(() => null)));
        if (!alive.current) return;
        const add = {};
        found.filter(Boolean).forEach(([id, msg]) => { add[id] = msg; });
        if (Object.keys(add).length) setStoredErrors((e) => ({ ...e, ...add }));
      }
    })();
  }, [pool, concurrency]);

  // ---- derived view ----
  const kindCounts = useMemo(() => {
    const c = { database: 0, file: 0, saas: 0 };
    pool.forEach((s) => { c[kindOf(s)] += 1; });
    return c;
  }, [pool]);
  const kinds = KINDS.filter(([k]) => kindCounts[k] > 0);

  const rows = useMemo(() => {
    const shown = pool.filter((s) => (kind === 'all' || kindOf(s) === kind) && matchesQuery(s, query));
    return sortRows(shown, sort.key, sort.dir, statusById);
  }, [pool, kind, query, sort, statusById]);

  const health = useMemo(() => {
    const n = { online: 0, offline: 0, untested: 0, testing: 0, recheck: 0 };
    Object.values(statusById).forEach((st) => {
      if (n[st.state] !== undefined) n[st.state] += 1;
      if (needsRecheck(st, now, staleAfterMs)) n.recheck += 1;
    });
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusById, staleAfterMs]);

  // ---- selection ----
  const toggle = (id) => {
    const key = String(id);
    const st = statusById[key];
    if (!st || !isPickable(st)) return;
    setNote('');
    const on = selectedRef.current.includes(key);
    if (multi) setSelected((ids) => (on ? ids.filter((x) => x !== key) : ids.concat(key)));
    else setSelected(on && live ? [] : [key]);
    // Choosing a source that has never been tested tests that one source, now.
    if (!on && st.state === 'untested') runTests([key]);
  };

  const shownPickable = rows.filter((s) => isPickable(statusById[String(s.id)]));
  const allShownOn = shownPickable.length > 0 && shownPickable.every((s) => selectedIds.includes(String(s.id)));
  const someShownOn = shownPickable.some((s) => selectedIds.includes(String(s.id)));
  const toggleAllShown = () => {
    const ids = shownPickable.map((s) => String(s.id));
    setNote('');
    if (allShownOn) setSelected((cur) => cur.filter((x) => !ids.includes(x)));
    else {
      setSelected((cur) => cur.concat(ids.filter((x) => !cur.includes(x))));
      runTests(ids.filter((id) => statusById[id].state === 'untested'));
    }
  };

  const waiting = selectedIds.filter((id) => statusById[id] && statusById[id].state === 'testing');
  // Only rows still on offer count: one the host just excluded (added, say) drops out.
  const poolIds = useMemo(() => new Set(pool.map((s) => String(s.id))), [pool]);
  const selectedRows = selectedIds.filter((id) => poolIds.has(id)).map((id) => byId[id]).filter(Boolean);
  useEffect(() => {
    if (live) return;
    setOwnSelected((ids) => {
      const next = ids.filter((id) => poolIds.has(id));
      return next.length === ids.length ? ids : next;
    });
  }, [poolIds, live]);
  // Why the confirm button is off for this user, if it is: a read-only account, or the host's own
  // rule (e.g. only administrators provision).
  const blockedReason = !canEdit && !readOnlyCanConfirm ? READ_ONLY_TITLE : confirmDisabledReason;
  const canConfirm = typeof onConfirm === 'function' && selectedRows.length > 0 && waiting.length === 0 && !busy && !loading
    && !blockedReason;

  const confirm = () => {
    if (!canConfirm) return;
    // In the order the table shows them (rows filtered out of view last), each with the verdict
    // seen here, so a start-time gate does not test the same row again.
    const at = (s) => {
      const i = rows.findIndex((r) => String(r.id) === String(s.id));
      return i === -1 ? rows.length : i;
    };
    onConfirm(selectedRows.slice().sort((a, b) => at(a) - at(b)).map((s) => {
      const o = overrides[String(s.id)];
      return o && o.state === 'online'
        ? { ...s, lastTestSuccess: true, lastTestAt: new Date(o.testedAt).toISOString() }
        : s;
    }));
  };

  // ---- keyboard ----
  const searchRef = useRef(null);
  const scrollRef = useRef(null);
  useEffect(() => {
    if (layout !== 'page') return undefined;
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      e.preventDefault();
      if (searchRef.current) searchRef.current.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [layout]);

  const onGridKey = (e) => {
    if (e.target !== scrollRef.current || rows.length === 0) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, Math.min(rows.length - 1, c + (e.key === 'ArrowDown' ? 1 : -1))));
    } else if (e.key === ' ' && cursor >= 0 && rows[cursor]) {
      e.preventDefault();
      toggle(rows[cursor].id);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      confirm();
    }
  };

  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  // ---- render ----
  const sortTh = (key, label, className) => {
    const on = sort.key === key;
    return (
      <th className={className} aria-sort={on ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
        <button type='button' className='sp-sort' data-active={on} onClick={() => onSort(key)}>
          {label} <span aria-hidden='true'>{on ? (sort.dir === 1 ? '↑' : '↓') : ''}</span>
        </button>
      </th>
    );
  };

  // Test actions exist only for accounts that may run one.
  const testLink = (id, label) => canEdit && (
    <button type='button' className='sp-link' onClick={(e) => { e.stopPropagation(); runTests([id]); }}
      data-testid={`${testId}-retest-${id}`}>{label}</button>
  );

  const statusCell = (s, st) => {
    const id = String(s.id);
    if (st.state === 'managed') {
      return <div className='sp-st'><span className='sp-st-line'><span className='sp-dot' /><span className='sp-st-label'>Managed by TDM</span></span></div>;
    }
    if (st.state === 'credentials') {
      return (
        <div className='sp-st'>
          <span className='sp-st-line'><span className='sp-dot sp-dot--bad' /><span className='sp-st-label'>Needs credentials</span></span>
          <span className='sp-st-actions'>
            <Link to={sourceEditorPath(id)} onClick={(e) => e.stopPropagation()}>Add credentials →</Link>
          </span>
        </div>
      );
    }
    if (st.state === 'testing') {
      return (
        <div className='sp-st'>
          <span className='sp-st-line'><span className='sp-dot sp-dot--testing' /><span className='sp-st-label'>Testing…</span></span>
          {st.prev && (
            <span className='sp-st-prev'>Last: {st.prev.state === 'online' ? 'online' : 'offline'} · {fmtRelative(st.prev.testedAt)}</span>
          )}
        </div>
      );
    }
    if (st.state === 'online') {
      return (
        <div className='sp-st'>
          <span className='sp-st-line' title={st.testedAt ? `Last successful test ${fmtDateTime(st.testedAt)}` : undefined}>
            <span className={`sp-dot ${st.stale ? 'sp-dot--stale' : 'sp-dot--ok'}`} />
            <span className='sp-st-label'>Online</span>
            <span className={`sp-st-time${st.stale ? ' sp-st-time--stale' : ''}`}>{st.testedAt ? fmtRelative(st.testedAt) : ''}</span>
          </span>
          {st.stale && canEdit && <span className='sp-st-actions'>{testLink(id, 'Retest')}</span>}
        </div>
      );
    }
    if (st.state === 'offline') {
      const err = st.error || storedErrors[id] || 'The last connection test failed.';
      return (
        <div className='sp-st'>
          <span className='sp-st-line' title={st.testedAt ? `Failed ${fmtDateTime(st.testedAt)}` : undefined}>
            <span className='sp-pill-off'>Offline</span>
            <span className='sp-st-time'>{st.testedAt ? fmtRelative(st.testedAt) : ''}</span>
          </span>
          <span className='sp-st-err' title={err} data-testid={`${testId}-error-${id}`}>{err}</span>
          <span className='sp-st-actions'>
            {testLink(id, 'Retry')}
            <Link to={sourceEditorPath(id)} onClick={(e) => e.stopPropagation()}>Fix connection →</Link>
          </span>
        </div>
      );
    }
    return (
      <div className='sp-st'>
        <span className='sp-st-line'><span className='sp-dot' /><span className='sp-st-label sp-muted'>Not tested</span></span>
        {canEdit && <span className='sp-st-actions'>{testLink(id, 'Test')}</span>}
      </div>
    );
  };

  const table = () => {
    if (loading) return <div className='sp-empty' data-testid={`${testId}-loading`}>{loading}</div>;
    if (pool.length === 0) {
      return (
        <div className='sp-empty' data-testid={`${testId}-empty`}>
          <strong>No data sources fit here</strong>
          {emptyMessage || (hidden.length
            ? `All ${plural(hidden.length, 'source')} were left out by the rules above.`
            : 'Add a data source first.')}
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <div className='sp-empty'>
          <strong>No match for “{query}”{kind !== 'all' ? ` in ${kind} sources` : ''}</strong>
          <button type='button' className='sp-link' onClick={() => { setQuery(''); setKind('all'); }}>Clear the search</button>
        </div>
      );
    }
    return (
      <table className='sp-grid' aria-multiselectable={multi}>
        <thead>
          <tr>
            <th className='sp-c-sel'>
              {multi ? (
                <input type='checkbox' aria-label='Select every source shown' checked={allShownOn}
                  ref={(el) => { if (el) el.indeterminate = someShownOn && !allShownOn; }}
                  disabled={shownPickable.length === 0} onChange={toggleAllShown} data-testid={`${testId}-select-all`} />
              ) : <span className='sp-sr-only'>Select</span>}
            </th>
            {sortTh('name', 'Name', 'sp-c-name')}
            {sortTh('engine', 'Engine', 'sp-c-eng')}
            {sortTh('location', 'Location', 'sp-c-loc')}
            {sortTh('status', 'Connection', 'sp-c-status')}
          </tr>
        </thead>
        <tbody>
          {rows.map((s, i) => {
            const id = String(s.id);
            const st = statusById[id];
            const pickable = isPickable(st);
            const on = selectedIds.includes(id);
            const loc = locationOf(s);
            const k = kindOf(s);
            const cls = ['sp-row', on && 'sp-row--on', !pickable && st.state !== 'testing' && 'sp-row--off', i === cursor && 'sp-row--cursor']
              .filter(Boolean).join(' ');
            return (
              <tr key={id} className={cls} aria-selected={on} aria-disabled={!pickable} data-testid={`${testId}-row-${id}`}
                onClick={(e) => { if (e.target.tagName !== 'INPUT') { setCursor(i); toggle(id); } }}
                onDoubleClick={multi || live ? undefined : () => {
                  if (!pickable || st.state === 'untested') return;
                  setSelected([id]);
                  if (typeof onConfirm === 'function' && !busy) onConfirm([s]);
                }}>
                <td className='sp-c-sel'>
                  <input type={multi ? 'checkbox' : 'radio'} tabIndex={-1} aria-label={`Select ${s.name}`}
                    checked={on} disabled={!pickable} onChange={() => toggle(id)} />
                </td>
                <td className='sp-c-name'>
                  <div className='sp-name'>
                    {s.name}
                    {s.readOnly && <span className='sp-tag'>read-only</span>}
                    {s.parentDataSourceId != null && <span className='sp-tag'>sub-source</span>}
                  </div>
                  <div className='sp-name-sub'>{engineText(s)}{loc.main ? ` · ${loc.main}` : ''}{loc.sub ? ` / ${loc.sub}` : ''}</div>
                </td>
                <td className='sp-c-eng'><span className={`sp-eng sp-eng--${k}`}>{engineText(s)}</span></td>
                <td className='sp-c-loc'>
                  <div className='sp-loc' title={[loc.main, loc.sub].filter(Boolean).join(' / ')}>
                    {loc.main || '—'}{loc.sub && <span className='sp-loc-sub'> / {loc.sub}</span>}
                  </div>
                </td>
                <td className='sp-c-status'>{statusCell(s, st)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const selText = multi
    ? (selectedRows.length ? <><strong>{plural(selectedRows.length, 'source')}</strong> selected</> : 'Nothing selected yet')
    : (selectedRows.length ? <>Selected: <strong className='sp-mono'>{selectedRows[0].name}</strong></> : 'Pick one source');
  let footNote = null;
  if (note) footNote = <span className='sp-note sp-note--bad'>{note}</span>;
  else if (waiting.length) {
    footNote = (
      <span className='sp-note'>
        Testing {waiting.length === 1 ? (byId[waiting[0]] || {}).name : plural(waiting.length, 'selected source')}…
      </span>
    );
  }
  const showFooter = typeof onConfirm === 'function' || typeof onCancel === 'function';

  const footer = showFooter && (
    <div className='sp-foot' data-testid={`${testId}-footer`}>
      <span className='sp-sel'>{selText}</span>
      {footNote}
      <span className='sp-actions'>
        {typeof onCancel === 'function' && (
          <button type='button' className='sp-btn' onClick={onCancel} data-testid={`${testId}-cancel`}>Cancel</button>
        )}
        {typeof onConfirm === 'function' && (
          <button type='button' className='sp-btn sp-btn--primary' disabled={!canConfirm} onClick={confirm}
            title={blockedReason || undefined}
            data-testid={`${testId}-confirm`}>
            {typeof busy === 'string' ? busy : verb}
          </button>
        )}
      </span>
    </div>
  );

  // Live mode keeps a note visible even without a footer (a failed pick was dropped).
  const liveNote = !showFooter && (note || waiting.length > 0) && <div className='sp-live-note'>{footNote}</div>;

  return (
    <section className={`sp sp--${layout}`} data-testid={testId}>
      {(title || subtitle) && (
        <header className='sp-head'>
          {title && <h2 className='sp-title'>{title}</h2>}
          {subtitle && <p className='sp-sub'>{subtitle}</p>}
        </header>
      )}
      <div className='sp-card'>
        <div className='sp-restrict'>
          <span className='sp-count'>
            Showing <strong>{loading ? '…' : pool.length}</strong> of {plural(rowsIn.length, 'source')}
          </span>
          {restrict.map((r) => <span key={r.label} className='sp-rchip'>{r.label}</span>)}
          {exclude.length > 0 && <span className='sp-rchip'>{plural(exclude.length, 'already added source')} left out</span>}
          {hidden.length > 0 && !loading && (
            <span className='sp-why'>
              <button type='button' className='sp-link' aria-expanded={showHidden} onClick={() => setShowHidden((v) => !v)}
                data-testid={`${testId}-why`}>
                {showHidden ? 'Hide the list' : `Why are ${hidden.length} hidden?`}
              </button>
            </span>
          )}
        </div>
        {showHidden && (
          <div className='sp-hidden' data-testid={`${testId}-hidden`}>
            <table><tbody>
              {hidden.map((h) => (
                <tr key={h.source.id}><td>{h.source.name}</td><td>{h.why}</td></tr>
              ))}
            </tbody></table>
          </div>
        )}
        <div className='sp-toolbar'>
          <div className='sp-search'>
            <label className='sp-sr-only' htmlFor={`${testId}-q`}>Search data sources</label>
            <input id={`${testId}-q`} ref={searchRef} type='search' autoComplete='off' value={query}
              placeholder='Search name, engine, host, database or path'
              onChange={(e) => { setQuery(e.target.value); setCursor(-1); }}
              onKeyDown={(e) => {
                // The picker can sit inside a host <form>: Enter must never submit it.
                if (e.key === 'Enter') e.preventDefault();
                if (e.key === 'Escape' && query) { e.preventDefault(); setQuery(''); }
                if (e.key === 'ArrowDown' && scrollRef.current) { e.preventDefault(); setCursor(0); scrollRef.current.focus(); }
              }}
              data-testid={`${testId}-search`} />
            {layout === 'page' && !query && <kbd>/</kbd>}
          </div>
          {kinds.length > 1 && (
            <div className='sp-kinds' role='group' aria-label='Kind'>
              <button type='button' aria-pressed={kind === 'all'} onClick={() => setKind('all')}>
                All<span className='sp-n'>{pool.length}</span>
              </button>
              {kinds.map(([k, label]) => (
                <button key={k} type='button' aria-pressed={kind === k} onClick={() => setKind(k)} data-testid={`${testId}-kind-${k}`}>
                  {label}<span className='sp-n'>{kindCounts[k]}</span>
                </button>
              ))}
            </div>
          )}
          {toolbar}
          <div className='sp-health' data-testid={`${testId}-health`}>
            <span className='sp-seg'><span className='sp-dot sp-dot--ok sp-dot--flat' />{health.online} online</span>
            {health.offline > 0 && <span className='sp-seg'><span className='sp-dot sp-dot--bad' />{health.offline} offline</span>}
            {health.untested > 0 && <span className='sp-seg'><span className='sp-dot' />{health.untested} not tested</span>}
            {health.testing > 0 && <span className='sp-seg'><span className='sp-dot sp-dot--testing' />{health.testing} testing</span>}
            <button type='button' className='sp-btn sp-btn--sm' disabled={health.recheck === 0 || !canEdit}
              title={canEdit ? 'Test the sources whose last test is more than a day old, or that were never tested' : READ_ONLY_TITLE}
              onClick={() => runTests(Object.keys(statusById).filter((id) => needsRecheck(statusById[id], Date.now(), staleAfterMs)))}
              data-testid={`${testId}-retest-stale`}>
              Retest stale ({health.recheck})
            </button>
          </div>
        </div>
        <div className='sp-scroll' ref={scrollRef} tabIndex={0} aria-label='Data sources' onKeyDown={onGridKey}
          style={layout === 'inline' ? { maxHeight } : undefined}>
          {table()}
        </div>
        {layout === 'inline' && footer}
        {liveNote}
      </div>
      {layout === 'page' && footer}
    </section>
  );
}

export default SourcePicker;
